import { useState } from "react";
import { useLang } from "@/lib/adminLang";
import { nok, kompakt, monthLabel } from "./budsjettUtils";
import { PlanEditor } from "./PlanEditor";
import MonthDrawer from "./MonthDrawer";

const Curve = ({ opening, months }) => {
  const W = 760, H = 150, P = 10;
  const vals = [opening, ...months.map((m) => m.closing)];
  const min = Math.min(0, ...vals);
  const max = Math.max(0, ...vals);
  const x = (i) => P + (i / Math.max(1, vals.length - 1)) * (W - 2 * P);
  const y = (v) => P + (1 - (v - min) / (max - min || 1)) * (H - 2 * P);
  const pts = vals.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  const zero = y(0);
  const warn = vals.some((v) => v < 0);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={`w-full h-[150px] bud-kurve ${warn ? "!text-amber-500" : ""}`} data-testid="cash-curve">
      <defs>
        <linearGradient id="budCashFyll" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.13" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1={P} x2={W - P} y1={zero} y2={zero} stroke="#8b9199" strokeOpacity="0.4" strokeDasharray="4 4" />
      <polygon points={`${pts} ${x(vals.length - 1)},${H - P} ${x(0)},${H - P}`} fill="url(#budCashFyll)" style={{ pointerEvents: "none" }} />
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" style={{ pointerEvents: "none" }} />
      {vals.map((v, i) => (v < 0 ? <circle key={i} cx={x(i)} cy={y(v)} r="2.6" fill="#D48A7E" style={{ pointerEvents: "none" }} /> : null))}
    </svg>
  );
};

export default function CashFlow({ data, scen, s, actions }) {
  const { t } = useLang();
  const { settings } = data;
  const cash = s.cash;
  const low = cash.lowest;
  const funding = cash.fundingRequired;
  const [innst, setInnst] = useState({
    openingCash: settings.openingCash ?? 0,
    eventMonth: settings.eventMonth || "",
    cashStartMonth: settings.cashStartMonth || "",
    cashEndMonth: settings.cashEndMonth || "",
  });
  const [kurve, setKurve] = useState(settings.ticketSalesCurve || []);
  const [visInnst, setVisInnst] = useState(false);
  const [maanedDrawer, setMaanedDrawer] = useState(null);

  const maks = Math.max(1, ...cash.months.map((m) => Math.max(m.inn, m.ut)));
  const siste = cash.months[cash.months.length - 1];

  return (
    <div data-testid="budget-cashflow" className="adm-rise">
      <div className="adm-strip grid-cols-2 md:grid-cols-3 mb-7">
        <div data-testid="cash-lowest-card">
          <p className={`bud-num text-[25px] tabular-nums leading-none ${low && low.amount < 0 ? "text-red-600" : "text-emerald-700"}`}>
            {low ? kompakt(low.amount) : "—"}
          </p>
          <p className="mt-2.5 text-[9.5px] font-semibold uppercase tracking-[0.18em] text-neutral-400">{t("Lowest projected cash")}</p>
          <p className="mt-1 text-[10.5px] text-neutral-400">{low ? `${monthLabel(low.month)} · ${scen}` : ""}</p>
        </div>
        <div data-testid="cash-funding-card">
          {funding ? (
            <>
              <p className="bud-num text-[25px] tabular-nums leading-none text-red-600">{kompakt(funding.amount)}</p>
              <p className="mt-2.5 text-[9.5px] font-semibold uppercase tracking-[0.18em] text-neutral-400">{t("Funding required")}</p>
              <p className="mt-1 text-[10.5px] text-neutral-400">{t("to stay cash-positive through")} {monthLabel(funding.month)}</p>
            </>
          ) : (
            <>
              <p className="bud-num text-[25px] tabular-nums leading-none text-emerald-700">0</p>
              <p className="mt-2.5 text-[9.5px] font-semibold uppercase tracking-[0.18em] text-neutral-400">{t("Funding required")}</p>
              <p className="mt-1 text-[10.5px] text-neutral-400">{t("No external funding required in")} {scen}</p>
            </>
          )}
        </div>
        <div data-testid="cash-closing-card">
          <p className={`bud-num text-[25px] tabular-nums leading-none ${siste?.closing < 0 ? "text-red-600" : ""}`}>
            {kompakt(siste?.closing || 0)}
          </p>
          <p className="mt-2.5 text-[9.5px] font-semibold uppercase tracking-[0.18em] text-neutral-400">{t("Closing cash")}</p>
          <p className="mt-1 text-[10.5px] text-neutral-400">{t("After")} {monthLabel(siste?.month)} · {t("opening")} {kompakt(cash.opening)}</p>
        </div>
      </div>

      {/* Cash balance curve */}
      <div className="adm-gruppe pt-4 pb-2 mb-7" data-testid="cash-curve-card">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400">{t("Cash balance")}</p>
          <p className="text-[10.5px] text-neutral-400 tabular-nums">
            {monthLabel(cash.months[0]?.month)} → {monthLabel(siste?.month)} · {t("payments incl. VAT")}
          </p>
        </div>
        <Curve opening={cash.opening} months={cash.months} />
        <div className="flex items-center justify-between mt-1.5 bud-mono text-[9px] uppercase tracking-[0.16em] text-neutral-400 tabular-nums" data-testid="cash-curve-caption">
          <span>{t("Lowest cash")} {low ? `${kompakt(low.amount)} · ${monthLabel(low.month)}` : "—"}</span>
          <span>{t("Closing")} {kompakt(siste?.closing || 0)} · {monthLabel(siste?.month)}</span>
        </div>
      </div>

      <div className="overflow-x-auto adm-gruppe">
        <table className="adm-table adm-table-flat" data-testid="cash-table">
          <thead>
            <tr>
              <th>{t("Month")}</th>
              <th className="adm-right">{t("In")}</th>
              <th className="adm-right">{t("Out")}</th>
              <th className="adm-right">{t("Net")}</th>
              <th className="adm-right">{t("Closing")}</th>
              <th className="w-[26%]">
                <span className="inline-flex items-center gap-3 normal-case tracking-normal font-normal">
                  <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-[2px] bg-emerald-500/70 inline-block" />{t("In")}</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-[2px] bg-neutral-400/70 inline-block" />{t("Out")}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {cash.months.map((m) => (
              <tr
                key={m.month}
                onClick={() => setMaanedDrawer(m)}
                className="cursor-pointer"
                data-testid={`cash-row-${m.month}`}
              >
                <td className="font-medium whitespace-nowrap">
                  {monthLabel(m.month)}
                  {low && m.month === low.month && (
                    <span className={`bud-radtag ${low.amount < 0 ? "text-red-500" : "text-amber-600"}`}>· {t("Lowest")}</span>
                  )}
                  {settings.eventMonth === m.month && (
                    <span className="bud-radtag text-neutral-400">· {t("Event")}</span>
                  )}
                </td>
                <td className="text-right tabular-nums text-emerald-700">{m.inn ? nok(m.inn) : "—"}</td>
                <td className="text-right tabular-nums text-neutral-600">{m.ut ? nok(m.ut) : "—"}</td>
                <td className={`text-right tabular-nums ${m.inn - m.ut < 0 ? "text-red-600" : "text-neutral-500"}`}>
                  {m.inn - m.ut === 0 ? "—" : nok(m.inn - m.ut)}
                </td>
                <td className={`text-right tabular-nums font-semibold ${m.closing < 0 ? "text-red-600" : ""}`}>{nok(m.closing)}</td>
                <td>
                  <div className="flex items-center gap-1 h-[10px]">
                    <div className="h-full bg-emerald-500/70 rounded-sm" style={{ width: `${(m.inn / maks) * 100}%` }} />
                    <div className="h-full bg-neutral-400/70 rounded-sm" style={{ width: `${(m.ut / maks) * 100}%` }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={() => setVisInnst((v) => !v)}
        className="adm-btn-ghost adm-btn-sm mt-5"
        data-testid="cash-settings-toggle"
      >
        {visInnst ? t("Hide settings") : t("Settings — period, opening cash & sales curve")}
      </button>

      {visInnst && (
        <div className="adm-gruppe pt-5 mt-3" data-testid="cash-settings">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="adm-label">
              {t("Opening cash")}
              <input type="number" value={innst.openingCash} onChange={(e) => setInnst((f) => ({ ...f, openingCash: e.target.value }))} className="adm-input" data-testid="cash-opening-input" />
            </label>
            <label className="adm-label">
              {t("From month")}
              <input type="month" value={innst.cashStartMonth} onChange={(e) => setInnst((f) => ({ ...f, cashStartMonth: e.target.value }))} className="adm-input" />
            </label>
            <label className="adm-label">
              {t("To month")}
              <input type="month" value={innst.cashEndMonth} onChange={(e) => setInnst((f) => ({ ...f, cashEndMonth: e.target.value }))} className="adm-input" />
            </label>
            <label className="adm-label">
              {t("Event month")}
              <input type="month" value={innst.eventMonth} onChange={(e) => setInnst((f) => ({ ...f, eventMonth: e.target.value }))} className="adm-input" />
            </label>
          </div>
          <p className="adm-label mt-4 mb-2">{t("Ticket sales curve (share of sales per month)")}</p>
          <PlanEditor plan={kurve} onChange={setKurve} testid="cash-curve-editor" />
          <button
            type="button"
            onClick={() => actions.patchSettings({ ...innst, openingCash: Number(innst.openingCash) || 0, ticketSalesCurve: kurve })}
            className="adm-btn adm-btn-sm mt-4"
            data-testid="cash-settings-save"
          >
            {t("Save settings")}
          </button>
        </div>
      )}

      {maanedDrawer && (
        <MonthDrawer
          entry={maanedDrawer}
          tx={(cash.tx || {})[maanedDrawer.month] || []}
          scen={scen}
          onClose={() => setMaanedDrawer(null)}
        />
      )}
    </div>
  );
}
